import { UnitOfTime } from '@ardoq/api-types';
import { ChartWindowState, Size } from '@ardoq/graph';
import {
  TimelineChartRenderingData,
  TimelineViewModelData,
  TimelineViewSettings,
  XAxisLabelRowProps,
  XAxisProps,
} from '../types';
import {
  readTimelineDomainRangeViewSetting,
  readTimelineGridPrecisionViewSetting,
} from '../calculations';
import { expandDomain, getNumberOfYearsForMultiYearTick } from '../grid/ticks';
import {
  getAvailableDomain,
  getGridPrecision,
  getScaleLimitsForGrid,
} from '../grid/utils';
import {
  DEFAULT_TIMELINE_SCALE_LIMITS,
  DEFAULT_WINDOW_CENTER,
} from '../consts';
import { isManualGridOption } from '../grid/calculations';
import {
  DOMAIN_RANGE_LABEL_FORMAT_OPTIONS,
  EMPTY_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA,
} from './consts';
import { getChartDimensions } from './calculations';
import { getPhasesScaler } from '../items/utils';
import {
  GRID_LABELS_BOTTOM_ROW_HEIGHT,
  GRID_LABELS_TOP_ROW_HEIGHT,
} from '../xAxis/consts';
import { formatIntlDate } from '../xAxis/utils';
import { getVisibleChartDomainWithScalers } from '../chart/calculations';
import { getLabelRowProps } from './utils';
import { clamp } from 'lodash';

type GetTimelineChartRenderingDataArgs = {
  viewSettings: TimelineViewSettings;
  clientRect: Size;
  userDefinedLabelsColumnWidth: number | null;
  chartWindow: ChartWindowState;
  timelineViewModelData: Pick<
    TimelineViewModelData,
    'viewModelData' | 'viewModelDataDomain'
  >;
};
const getTimelineChartRenderingData = ({
  timelineViewModelData: { viewModelData, viewModelDataDomain },
  viewSettings,
  clientRect,
  userDefinedLabelsColumnWidth,
  chartWindow,
}: GetTimelineChartRenderingDataArgs): TimelineChartRenderingData => {
  const domainRangeViewSetting = readTimelineDomainRangeViewSetting(
    viewSettings.domainRange
  );
  const gridPrecisionViewSettingsValue = readTimelineGridPrecisionViewSetting(
    viewSettings.gridPrecision
  );

  const isClientRectEmpty = clientRect.width === 0 || clientRect.height === 0;

  if (isClientRectEmpty) {
    return EMPTY_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA;
  }

  const availableExpandedDomain = expandDomain(
    getAvailableDomain(viewModelDataDomain, domainRangeViewSetting),
    domainRangeViewSetting
  );

  const { dimensions, labelsColumnMinWidth } = getChartDimensions({
    viewModelData,
    userDefinedLabelsColumnWidth,
    ...clientRect,
  });

  const adjustedGridPrecision = getGridPrecision({
    gridPrecisionViewSettingsValue,
    availableExpandedDomain,
  });

  const chartWidth = dimensions.chart.width;

  const numberOfUnitsForMultiUnitTick =
    (!isManualGridOption(gridPrecisionViewSettingsValue) &&
      adjustedGridPrecision === UnitOfTime.YEAR &&
      availableExpandedDomain &&
      chartWidth &&
      getScaleLimitsForGrid(
        adjustedGridPrecision,
        chartWidth,
        availableExpandedDomain
      ).max < 1 &&
      getNumberOfYearsForMultiYearTick(availableExpandedDomain, chartWidth)) ||
    0;

  const scalingLimitsForAdjustedGrid = chartWidth
    ? getScaleLimitsForGrid(
        adjustedGridPrecision,
        chartWidth,
        availableExpandedDomain,
        Boolean(numberOfUnitsForMultiUnitTick)
      )
    : DEFAULT_TIMELINE_SCALE_LIMITS;

  const windowScale = scalingLimitsForAdjustedGrid
    ? clamp(
        chartWindow.windowScale,
        scalingLimitsForAdjustedGrid.min,
        scalingLimitsForAdjustedGrid.max
      )
    : chartWindow.windowScale;

  const windowCenter =
    windowScale === 1 ? DEFAULT_WINDOW_CENTER : chartWindow.windowCenter;

  const { scaleX, unscaleX, visibleDomain, todayPosition } =
    getVisibleChartDomainWithScalers({
      windowCenter,
      windowScale,
      availableExpandedDomain,
      dimensions,
    });

  const isDailyGrid = adjustedGridPrecision === UnitOfTime.DAY;

  const isTopRowVisible =
    isDailyGrid ||
    adjustedGridPrecision === UnitOfTime.WEEK ||
    adjustedGridPrecision === UnitOfTime.QUARTER;

  const xAxisContainerHeight =
    GRID_LABELS_BOTTOM_ROW_HEIGHT + // bottom row
    (isTopRowVisible ? GRID_LABELS_TOP_ROW_HEIGHT : 0) + // top row
    (isDailyGrid ? 0 : GRID_LABELS_BOTTOM_ROW_HEIGHT); // domainRange row height, hidden only in daily grid

  const [visibleDomainStart, visibleDomainEnd] = visibleDomain;

  // visible domain row is present in all grids other than daily
  const visibleDomainRowLabel = isDailyGrid
    ? null
    : `${formatIntlDate(
        visibleDomainStart,
        DOMAIN_RANGE_LABEL_FORMAT_OPTIONS
      )} - ${formatIntlDate(
        visibleDomainEnd,
        DOMAIN_RANGE_LABEL_FORMAT_OPTIONS
      )}`;

  // top row is present only in certain grid precisions
  const topLabelRowProps: XAxisLabelRowProps | null = getLabelRowProps({
    scaleX,
    visibleDomain,
    adjustedGridPrecision,
    dimensions,
    numberOfUnitsForMultiUnitTick,
    isTopRow: true,
  });

  // bottom row is always present
  const bottomLabelRowProps: XAxisLabelRowProps = getLabelRowProps({
    scaleX,
    visibleDomain,
    adjustedGridPrecision,
    dimensions,
    numberOfUnitsForMultiUnitTick,
    isTopRow: false,
  }) || {
    rowHeight: GRID_LABELS_BOTTOM_ROW_HEIGHT,
    dimensions,
    noVisibleTicksRowLabel: null,
    tickLabelsToDisplay: [],
  };

  const xAxisProps: XAxisProps = {
    bottomLabelRowProps: bottomLabelRowProps,
    topLabelRowProps: topLabelRowProps,
    xAxisContainerHeight,
    visibleDomainRowLabel,
  };

  const phasesScaler = getPhasesScaler({
    chartWidth,
    windowScale,
    windowCenter,
    availableExpandedDomain,
  });

  return {
    timelineChartRangeConstraints: {
      availableExpandedDomain,
      scalingLimitsForAdjustedGrid,
    },
    timelineChartProperties: {
      chartWindow: {
        windowCenter,
        windowScale,
      },
      clientRect,
      visibleDomain,
      adjustedGridPrecision,
      dimensions,
      xAxisProps,
      unscaleX,
      todayPosition,
      labelsColumnMinWidth,
      userDefinedLabelsColumnWidth,
      phasesScaler,
      crosshairXProps: null,
    },
  };
};

export { getTimelineChartRenderingData };
